import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import RefreshAction from "PFApp/activities/show/parts/refresh_action";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import DropdownButton from "PFComponents/dropdown/dropdown_button";
import ToastWrap from "PFComponents/growl/toast";
import { Icon } from "PFComponents/icon";
import useWindowSize from "PFCore/helpers/use_window_size";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useShortlistDecision } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import { useShortlistDelete } from "PFCore/hooks/queries/shortlists/use_shortlist_delete";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { ApiRoute } from "PFCore/utilities/routes";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import css from "./shortlists_actions.module.scss";

const ShortlistActions = () => {
  const [undoItems, setUndoItems] = useState([]);
  const [showClearShortlistConfirm, setClearShortlistConfirm] = useState(false);
  const [showUnbiasedConfirm, setShowUnbiasedConfirm] = useState(false);
  const { windowWidth } = useWindowSize();
  const location = useLocation();
  const { t } = useTranslation("activities");
  const { invalidate: invalidateActivities } = useActivityInvalidate();
  const { mutateAsync: createShortlist } = useShortlistCreate({}, false);
  const { mutateAsync: makeShortlistDecision } = useShortlistDecision({}, false);
  const { invalidateShortlists } = useShortlistsInvalidate();

  const { task, shortlists, clearComparisonToast, isUnbiasedEnabled, isActivityUnbiased } =
    useActivityPageContext();

  const { mutateAsync: deleteShortlist } = useShortlistDelete({ withInvalidate: false, activityId: task.id });

  const { dispatch } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();

  const isCompleted = task.state === "complete";

  const unRemoveShortlistItems = useCallback(() => {
    const promises = undoItems.map((item) => {
      const { activityId, profileId } = item.data;
      switch (item.state) {
        case "accepted":
          return createShortlist({ activityId, profileId }).then((newShortlist) =>
            makeShortlistDecision({ action: "accept", shortlistId: newShortlist.id })
          );
        case "declined":
          return createShortlist({ activityId, profileId }).then((newShortlist) =>
            makeShortlistDecision({ action: "decline", shortlistId: newShortlist.id })
          );
        default:
          return createShortlist({ activityId, profileId });
      }
    });

    Promise.all(promises).then(() => {
      invalidateShortlists();
    });
    setUndoItems([]);
  }, [undoItems]);

  const clearShortlist = () => {
    setClearShortlistConfirm(true);
  };

  const handleClearShortlist = () => {
    clearComparisonToast();

    const promises = shortlists.map((item) => deleteShortlist(item.id));

    const newUndoItems = shortlists.map((item) => ({
      state: item.state,
      data: {
        activityId: item.activity_id,
        profileId: item.profile.id
      }
    }));

    Promise.all(promises)
      .then(() => {
        invalidateShortlists();
        setUndoItems(newUndoItems);
        window.setTimeout(() => setUndoItems([]), 30 * 1000);
      })
      .catch(() => {
        invalidateShortlists();
      });
  };

  const options = [
    {
      id: "clear_all",
      displayElement: <span className={css.danger}>{t("show.shortlist.clearShortlist")}</span>,
      item: clearShortlist
    }
  ];

  const taskReviewerId = task?.reviewers?.filter((reviewer) => reviewer.profile_id === currentProfile.id);

  const revealBlindMode = () => {
    $.ajax({
      method: "PUT",
      url: ApiRoute(`/api/reviewers/${taskReviewerId?.[0]?.id}`),
      api_version: 2,
      contentType: "application/json",
      data: JSON.stringify({ unbiased: false })
    })
      .then(() => {
        invalidateActivities([task.id]);
      })
      .catch(() => {
        dispatch({
          type: GROWL_ACTIONS.GROWL_RENDER,
          payload: {
            message: t("somethingWentWrong"),
            kind: "error"
          }
        });
      });
  };

  // Style on the dropdown just to keep the margin left if there is not other buttons and is in mobile
  const isDropDownAlone = !(isUnbiasedEnabled && isActivityUnbiased && !!shortlists);
  const dropDownStyle =
    isDropDownAlone && windowWidth < 767
      ? {
          left: 0
        }
      : {};
  return (
    <>
      <div className={css.buttonWrapper}>
        {showUnbiasedConfirm && (
          <ConfirmModal handleOK={revealBlindMode} handleClose={() => setShowUnbiasedConfirm(false)}>
            {t("show.shortlist.wantToRevealShortlist")}
          </ConfirmModal>
        )}
        <div className={css.buttons}>
          {isUnbiasedEnabled && isActivityUnbiased && (
            <Button text={t("show.shortlist.revealIdentity")} onClick={() => setShowUnbiasedConfirm(true)} />
          )}
          {!isCompleted && task.coowned && (
            <RefreshAction
              activityId={task.id}
              onUpdate={() => {
                invalidateShortlists();
              }}
            />
          )}
          {shortlists && shortlists.length > 1 && (
            <ButtonLink
              href={{
                pathname: `/activities/${task.id}/compare/details`,
                state: { prevPath: location.pathname }
              }}
              kind="secondary"
              style={{ minWidth: 140 }}
            >
              <Icon name="compare" size="sm" />
              {t("show.shortlist.compareAll")}
            </ButtonLink>
          )}
          {task.coowned && shortlists && shortlists.length > 0 && (
            <DropdownButton
              dropDownStyle={{ minWidth: 135, ...dropDownStyle }}
              buttonKind="blank"
              options={options}
              handleChange={(fn) => fn && fn()}
            >
              <Icon name="menu-horizontal" size="sm" />
            </DropdownButton>
          )}
        </div>
      </div>

      {showClearShortlistConfirm && (
        <ConfirmModal handleOK={handleClearShortlist} handleClose={() => setClearShortlistConfirm(false)}>
          {t("show.shortlist.wantClearAllProfiles")}
        </ConfirmModal>
      )}
      <ToastWrap
        kind="snackbar"
        show={undoItems.length > 0}
        text={t("show.shortlist.clearedShortlist")}
        action={unRemoveShortlistItems}
        actionText="Undo"
      />
    </>
  );
};

export default ShortlistActions;
